import { type ResponseContext } from '@linktivity/ars-neo';
import { message } from '@linktivity/link-ui';
import {
  UNAUTHORIZED,
  AUTHORIZATION_HEADER,
  AGENT_HEADER
} from '@link/share/constants';
import { auth } from '@link/triplabo/stores';

const BASE_PATH = import.meta.env.VITE_APP_ARS_API as string;

const AGENT_ID = (import.meta.env.VITE_APP_MOCK_AGENT as string) || 'triplabo';

export interface ServiceConfigOptions<T, C = unknown> {
  Configuration: new (config: C) => T;
  basePath?: string;
}

const refreshToken = async (
  init: RequestInit,
  url: string,
  fetch: (input: RequestInfo, init?: RequestInit) => Promise<Response>
) => {
  const accessToken = await auth.refreshExpiredToken();
  if (accessToken) {
    return fetch(url, init);
  }
};

// Grpc-Metadata-Agent
// Grpc-Metadata-Authorization
export function createServiceConfig<T, C = unknown>({
  Configuration,
  basePath = BASE_PATH
}: ServiceConfigOptions<T, C>) {
  return new Configuration({
    basePath,
    middleware: [
      {
        pre: async (context: { init: RequestInit }) => {
          const requestHeaders: HeadersInit = new Headers({
            'Content-Type': 'application/json'
          });
          requestHeaders.set(AUTHORIZATION_HEADER, auth.getAccessToken());
          requestHeaders.set(AGENT_HEADER, AGENT_ID);
          context.init.headers = requestHeaders;
        },
        post: async (context: ResponseContext) => {
          if (context.response.status === UNAUTHORIZED) {
            // TODO: replace error code with actual error code
            const clonedResponse = context.response.clone();
            const data = await clonedResponse.json();
            if (data.error_id === 'EXPIRED_TOKEN') {
              return refreshToken(context.init, context.url, context.fetch);
            } else {
              auth.clearUser();
              const currentPath = window.location.pathname;
              const searchParams = window.location.search;
              window.location.replace(
                `/login?redirect=${encodeURIComponent(currentPath + searchParams)}`
              );
            }
          } else if (!context.response.ok) {
            const clonedResponse = context.response.clone();
            try {
              const data = await clonedResponse.json();
              message.error(data?.message || data?.details?.[0]?.reason, 10);
            } catch {
              // If JSON parsing fails, ignore
            }
            // Return undefined to let the SDK handle the response normally
          }
        }
      }
    ]
  } as C);
}
